import {
  generateChangeText,
  repeatTabs,
  sortMapToArray,
} from './textDiffFormatting'
import { VariablesByType, VariablesDiffs } from './variableDiffTypes'

export const generateVariableList = (variables: VariablesByType): string => {
  const variableList = sortMapToArray(variables)
  if (variableList.length === 0) {
    return `${repeatTabs(1)}None`
  }
  return variableList
    .map(
      ([type, variables]) =>
        `${repeatTabs(1)}Type: ${type}\n${repeatTabs(2)}Variables: ${variables.join(', ')}`,
    )
    .join('\n')
}

export const generateVariableChanges = (
  variablesChangesByType: VariablesDiffs,
): string => {
  const variablesChanges = sortMapToArray(variablesChangesByType)
  if (variablesChanges.length === 0) {
    return `${repeatTabs(1)}None`
  }
  return variablesChanges
    .map(
      ([type, variables]) =>
        `\n${repeatTabs(1)}Type: ${type}${Array.from(variables.entries())
          .toSorted(([a], [b]) => a.localeCompare(b))
          .map(([variableName, diffs]) => {
            const diffTexts = []
            if (diffs.name) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.name.from,
                  to: diffs.name.to,
                  label: 'Name',
                }),
              )
            }
            if (diffs.type) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.type.from,
                  to: diffs.type.to,
                  label: 'Type',
                }),
              )
            }
            if (diffs.intervalUnit) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.intervalUnit.from,
                  to: diffs.intervalUnit.to,
                  label: 'Interval unit',
                }),
              )
            }
            if (diffs.intervals) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.intervals.from,
                  to: diffs.intervals.to,
                  label: 'Intervals',
                }),
              )
            }
            if (diffs.transitionFunctions) {
              diffTexts.push(`${repeatTabs(3)}Transition functions:`)
              diffTexts.push(
                ...diffs.transitionFunctions.rows.map(
                  ({ boundaryLabel, from, to }) =>
                    generateChangeText({
                      from,
                      to,
                      label: boundaryLabel,
                      tabs: 4,
                    }),
                ),
              )
            }
            if (diffs.parameterizationType) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.parameterizationType.from,
                  to: diffs.parameterizationType.to,
                  label: 'Parameterization type',
                }),
              )
            }
            if (diffs.state) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.state.from.join(', '),
                  to: diffs.state.to.join(', '),
                  label: 'State',
                }),
              )
            }
            if (diffs.optimalStateName) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.optimalStateName.from,
                  to: diffs.optimalStateName.to,
                  label: 'Optimal state',
                }),
              )
            }
            if (diffs.probabilities) {
              diffTexts.push(`${repeatTabs(3)}Probabilities: changed`)
            }
            if (diffs.granularity) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.granularity.from,
                  to: diffs.granularity.to,
                  label: 'Granularity',
                }),
              )
            }
            if (diffs.comment) {
              diffTexts.push(
                `${repeatTabs(3)}Comment text:\n${diffs.comment.textDiff}`,
              )
            }
            if (diffs.tags) {
              diffTexts.push(
                generateChangeText({
                  from: diffs.tags.from.join(', ') || '(empty)',
                  to: diffs.tags.to.join(', ') || '(empty)',
                  label: 'Tags',
                }),
              )
            }
            if (diffs.definition) {
              diffTexts.push(
                `${repeatTabs(3)}Definition text:\n${diffs.definition.textDiff}`,
              )
            }
            if (diffs.references) {
              diffTexts.push(
                `${repeatTabs(3)}References text:\n${diffs.references.textDiff}`,
              )
            }
            if (diffs.formula) {
              diffTexts.push(
                `${repeatTabs(3)}Formula text:\n${diffs.formula.textDiff}`,
              )
            }
            if (diffs.prevalenceExplanation) {
              diffTexts.push(
                `${repeatTabs(3)}Prevalence Explanation text:\n${diffs.prevalenceExplanation.textDiff}`,
              )
            }
            if (diffs.arrowComment) {
              diffTexts.push(`${repeatTabs(3)}Arrows comment:`)
              diffTexts.push(
                ...diffs.arrowComment.map(
                  ({ textDiff, inboundVariableName }) =>
                    `\n${repeatTabs(4)}Inbound variable: ${inboundVariableName}\n${textDiff}`,
                ),
              )
            }
            return `\n${repeatTabs(2)}Variable: ${variableName}\n${diffTexts.join('\n')}`
          })
          .join('\n')}`,
    )
    .join('\n')
}
